import { FeatureAvailabilityToken, UserContextToken, WindowToken } from '../../injection-tokens';
import Container, { Service } from 'typedi';
import DocumentHelper from '../../../Modules/Utils/DocumentHelper';
import { LoggerProvider } from '../logger';
import { ILogger } from '@sparkware/uc-sdk-core';
import { IUserContext } from '../user-context/user-context-interface';
import { Features, SmartBannerNotRequiredFtdBrands } from '../../models/enums/general-enums';
import { Utils } from '../utils';
import { IFeatureAvailability } from '../feature/feature-availability/feature-availability-interface';

@Service()
export class SmartBannerService {
    private readonly _logger: ILogger;
    private readonly _window: Window;
    private readonly _userContext: IUserContext;
    private readonly _utils: Utils;
    private readonly _featureAvailability: IFeatureAvailability;
    private _isSmartBannerRegistered = false;

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('SmartBannerService');
        this._window = Container.get(WindowToken);
        this._userContext = Container.get(UserContextToken);
        this._utils = Container.get(Utils);
        this._featureAvailability = Container.get(FeatureAvailabilityToken);
        this._logger.debug('Initialise smart banner service');
    }

    public registerSmartBannerService(): void {
        if (this._isSmartBannerEnabled()) {
            this._addSmartBannerFeature();
        } else {
            this._logger.debug('Smart Banner is not enabled, not initialising');
        }
    }

    private _isSmartBannerEnabled(): boolean {
        const isMobile = this._window.pageContextManager.getDeviceData().isMobile;
        const { isFtd } = this._window.pageContextManager.getUserData();
        const smartBannerEnabled = this._featureAvailability.IsFeatureEnabled(
            Features.SMART_BANNER,
        );
        return (
            smartBannerEnabled &&
            ((this._userContext.IsAuthenticated && isFtd) || this._isSmartBannerNotRequiredFtd()) &&
            isMobile &&
            !this._utils.findIfIsNative()
        );
    }

    private _isSmartBannerNotRequiredFtd = (): boolean => {
        const { brandId } = this._window.pageContextManager.getBrandData();
        return !!SmartBannerNotRequiredFtdBrands.find((brand) => brand === brandId);
    };

    private _addSmartBannerFeature() {
        if (this._isSmartBannerRegistered) {
            this._logger.debug('Smart banner feature already registered, skipping');
            return;
        }

        this._isSmartBannerRegistered = true;
        DocumentHelper.onDomReady(() => {
            this._appendSmartBannerScript();
        });
    }

    private _appendSmartBannerScript = () => {
        const { key } = this._window.pageContextManager.getSmartBannerData();

        const smartBannerScript: string = `!function(t,e,n,s,a,c,i,o,p){t.AppsFlyerSdkObject=a,t.AF=t.AF||function(){
            (t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))},
            t.AF.id=t.AF.id||i,t.AF.plugins={},o=e.createElement(n),p=e.getElementsByTagName(n)[0],o.async=1,
            o.src="https://websdk.appsflyersdk.com/?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:""),
            p.parentNode.insertBefore(o,p)}(window,document,"script",0,"AF","banners",{banners: {key: "${key}"}})
            AF('banners', 'showBanner')`;

        const script = document.createElement('script');
        script.setAttribute('data-ot-ignore', '');
        script.innerHTML = smartBannerScript;

        this._window.document.head.appendChild(script).parentNode?.removeChild(script);
    };
}
